import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  tmsApi,
  type CreateOrderTaskCargoItemRequest,
  type OrderTaskCargoItem,
  type UpdateOrderTaskCargoItemRequest,
} from '../client';
import { orderTaskKeys } from './useOrderTasks';

/**
 * Stable query-key factory for cargo-item caches. Items are scoped to a
 * task, so the natural cache key is the parent task id — that's also
 * how the BE list endpoint accepts the filter.
 *
 * <p>
 * Mutations also invalidate the per-order task list (via
 * {@link orderTaskKeys.byOrder}) because `OrderTask.cargoItems` is
 * embedded in the task DTO, so a cargo edit would otherwise leave the
 * Tasks panel rendering a stale embedded list until the next refetch.
 * </p>
 */
export const orderTaskCargoItemKeys = {
  all: ['tms', 'orderTaskCargoItem'] as const,
  byTask: (orderTaskId: string) =>
    ['tms', 'orderTaskCargoItem', 'task', orderTaskId] as const,
};

/**
 * Fetch every cargo item on a task, ordered by `itemIndex` (BE-side).
 * Pass `null` to keep the query disabled (e.g. while the task id is
 * still resolving from a parent selection).
 */
export function useOrderTaskCargoItems(
  orderTaskId: string | null,
  options?: Omit<
    UseQueryOptions<OrderTaskCargoItem[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrderTaskCargoItem[], Error>({
    queryKey: orderTaskId
      ? orderTaskCargoItemKeys.byTask(orderTaskId)
      : orderTaskCargoItemKeys.all,
    queryFn: () => {
      if (!orderTaskId) throw new Error('No order task id');
      return tmsApi.getOrderTaskCargoItems(orderTaskId);
    },
    enabled: !!orderTaskId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * POST a new cargo item on a task. Caller passes `orderId` alongside the
 * body so we can also invalidate the per-order task list — the Tasks
 * panel renders cargo items embedded under each task and would
 * otherwise miss the new row until the next manual refresh.
 */
export function useCreateOrderTaskCargoItem() {
  const qc = useQueryClient();
  return useMutation<
    OrderTaskCargoItem,
    Error,
    { orderId: string; body: CreateOrderTaskCargoItemRequest }
  >({
    mutationKey: [...orderTaskCargoItemKeys.all, 'create'],
    mutationFn: ({ body }) => tmsApi.createOrderTaskCargoItem(body),
    onSuccess: (_item, { orderId, body }) => {
      qc.invalidateQueries({
        queryKey: orderTaskCargoItemKeys.byTask(body.orderTaskId),
      });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byId(body.orderTaskId) });
    },
  });
}

/**
 * PUT an existing cargo item. The wire body only carries the cargo item
 * id, so the caller passes `orderId` + `orderTaskId` for cache
 * invalidation — the embedded task DTO needs refreshing alongside the
 * per-task cargo list.
 */
export function useUpdateOrderTaskCargoItem() {
  const qc = useQueryClient();
  return useMutation<
    OrderTaskCargoItem,
    Error,
    {
      orderId: string;
      orderTaskId: string;
      body: UpdateOrderTaskCargoItemRequest;
    }
  >({
    mutationKey: [...orderTaskCargoItemKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateOrderTaskCargoItem(body),
    onSuccess: (_item, { orderId, orderTaskId }) => {
      qc.invalidateQueries({
        queryKey: orderTaskCargoItemKeys.byTask(orderTaskId),
      });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byId(orderTaskId) });
    },
  });
}

/**
 * DELETE a cargo item by id. Caller passes `orderId` + `orderTaskId` so
 * both the per-task cargo cache and the embedded copy in the parent
 * task DTO get invalidated.
 */
export function useDeleteOrderTaskCargoItem() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { orderId: string; orderTaskId: string; cargoItemId: string }
  >({
    mutationKey: [...orderTaskCargoItemKeys.all, 'delete'],
    mutationFn: ({ cargoItemId }) => tmsApi.deleteOrderTaskCargoItem(cargoItemId),
    onSuccess: (_res, { orderId, orderTaskId }) => {
      qc.invalidateQueries({
        queryKey: orderTaskCargoItemKeys.byTask(orderTaskId),
      });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byId(orderTaskId) });
    },
  });
}
